import React, { useEffect, useState } from 'react';
import { Report, service, factories } from 'powerbi-client';
import { FilterType, IBasicFilter } from 'powerbi-models';
import usePBIEmbedded from './usePbiReport';
import { ReportContainer } from './PBIReport.styles';
import { useVesselStore } from '../../context/VesselContext';
import { useAuthStore } from '../../context/AuthContext';
import { usePbiReportPagesStore } from 'src/context/PbiReportsContext';

type Props = {
    selectedReport: string;
    selectedSection: string;
    checkToken: () => void;
};

async function runUpdate(report: Report, filter: IBasicFilter) {
    await report.setFilters([filter]);
}

const powerbi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);

const PBIReport: React.FC<Props> = ({ selectedSection, checkToken }) => {
    const reportContainerId = 'PBIReport';
    const { getEmbeddedConfiguration } = usePBIEmbedded();
    const { pbiAuthorization } = useAuthStore();
    const { selectedVessel } = useVesselStore();
    const { reportPages, setReportPages, selectedReport } = usePbiReportPagesStore();

    const [currentReport, setCurrentReport] = useState<string | null>(null);
    const [report, setReport] = useState<Report>(null);

    useEffect(() => {
        // report is already loaded, update filter on vessel change
        if (!pbiAuthorization || !report) return;
        const useFilter = !pbiAuthorization.reports.find((r) => r.reportName === selectedReport)?.smRestricted;
        if (!useFilter) return;
        checkToken();
        if (selectedVessel?.imoNumber && report && pbiAuthorization) {
            runUpdate(report, GetVesselFilter(selectedVessel.imoNumber));
            report.getPages().then((pages) => {
                setReportPages(pages);
            });
        }
    }, [selectedVessel]);

    useEffect(() => {
        // initial load of the report or change of selected report
        checkToken();
        const embedContainer = document.getElementById(reportContainerId);
        if (
            (!!report && selectedReport === currentReport) ||
            !embedContainer ||
            !pbiAuthorization?.reports ||
            !selectedVessel?.imoNumber ||
            !selectedReport
        )
            return;
        if (selectedReport !== currentReport) powerbi.reset(embedContainer);

        const useFilter = !pbiAuthorization.reports.find((r) => r.reportName === selectedReport)?.smRestricted;

        const embedConfiguration = getEmbeddedConfiguration(
            pbiAuthorization,
            selectedReport,
            useFilter ? GetVesselFilter(selectedVessel.imoNumber) : null,
            selectedSection
        );

        if (!embedConfiguration) return;
        const r = powerbi.embed(embedContainer, embedConfiguration) as Report;
        setReport(r);
        setCurrentReport(selectedReport);

        r.on('rendered', () => {
            r.getPages().then((pages) => {
                setReportPages(pages);
            });
        });
    }, [pbiAuthorization, selectedVessel, selectedReport]);

    useEffect(() => {
        if (report && reportPages && selectedSection) report.setPage(selectedSection);
    }, [selectedSection]);

    const GetVesselFilter = (imo: number): IBasicFilter => {
        return {
            $schema: 'http://powerbi.com/product/schema#basic',
            target: {
                table: 'dimVessel',
                column: 'ImoNumber',
            },
            operator: 'In',
            values: [imo],
            filterType: FilterType.Basic,
        };
    };

    return <ReportContainer id={reportContainerId}></ReportContainer>;
};
export default PBIReport;
